import { useState, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { isEmpty, isEqual, cloneDeep } from 'lodash'

import { TileDatagrid } from '../../../dis-v2/_components/TileDatagrid'

import { off, onValue, ref } from 'firebase/database'
import database from '../../../../../firebase-config'

import { useActivityViewQuery } from './helper/reducers/acitivityViewQuery'
import { useRecordQuery } from '../utils/tileRecordQuery'

import { getSelectedGridRecord, parsedDataGridColumns } from '../../../../data/dataHelpers'
import { getFilteredRows } from '../../../../data/dataHelpers'
import { sleep } from 'utils/functions/helpers'

function buildProjectFormConditionQuery(key, seachValues) {
	return {
		target: key,
		type: 'CT',
		values: [...new Set(seachValues)],
		join: 'AND',
	}
}

const AVAILABLE_COLUMNS = ['@ActivityReceiveTime', 'type', 'formName', 'userName']

export function JobActivityViewComponent(props) {
	const { tile, activityJobFieldValue, selectedJobForm, selectedJobActivityForm } = props
	const { viewData, allColumns, serverFilter, viewLoading } = useActivityViewQuery({
		tile,
	})

	const { environment } = useSelector((state) => state)
	const [t] = useTranslation()

	// grid section
	const [gridColumns, setGridColumns] = useState([])
	const [currentRecords, setCurrentRecords] = useState([])

	const clientFilter = useMemo(() => {
		if (
			isEmpty(selectedJobForm) ||
			isEmpty(selectedJobActivityForm) ||
			isEmpty(viewData?.queries?.[0]?.filter?.conditions)
		)
			return []
		const currentConditions = cloneDeep(viewData?.queries?.[0]?.filter?.conditions)
		const newConditions = currentConditions.map((condition) => {
			if (condition.target !== '@ActivityReceiveTime') {
				return condition
			}

			condition.preset = 'ALL'
			return condition
		})

		const projectNameCondition = buildProjectFormConditionQuery('projectName', [
			selectedJobForm?.projectName,
			selectedJobActivityForm?.projectName,
		])
		newConditions.push(projectNameCondition)

		const formNameCondition = buildProjectFormConditionQuery('formName', [
			selectedJobForm?.name || selectedJobForm?.formName,
			selectedJobActivityForm?.name || selectedJobActivityForm?.formName,
		])
		newConditions.push(formNameCondition)
		if (!isEmpty(activityJobFieldValue)) {
			const jobIdCondition = {
				join: 'AND',
				target: 'recordName',
				type: 'CT',
				values: [activityJobFieldValue],
			}
			newConditions.push(jobIdCondition)
		}

		return newConditions
	}, [viewData, selectedJobForm, selectedJobActivityForm, activityJobFieldValue])

	const { records, recordsLoading, refetchRecords } = useRecordQuery({
		viewKey: isEmpty(clientFilter) ? null : viewData?.key,
		viewType: isEmpty(clientFilter) ? null : viewData?.type,
		clientFilter: isEmpty(clientFilter) ? null : { conditions: clientFilter },
	})

	useEffect(() => {
		const parsedGridColumns = parsedDataGridColumns(environment, allColumns)
		if (isEqual(gridColumns, parsedGridColumns)) return
		const availableColumns = parsedGridColumns.filter((col) =>
			AVAILABLE_COLUMNS.includes(col.field)
		)
		setGridColumns(availableColumns)
	}, [JSON.stringify(allColumns)])

	useEffect(() => {
		if (recordsLoading) return
		if (isEqual(records, currentRecords)) return
		setCurrentRecords(records)
	}, [recordsLoading, records])

	const gridLoading = useMemo(() => {
		return viewLoading || recordsLoading
	}, [viewLoading, recordsLoading])

	const currentRows = useMemo(() => {
		if (!currentRecords?.length || !allColumns?.length) return []

		const allRows = getSelectedGridRecord({
			records: currentRecords,
			columns: allColumns,
			environment,
			columnCheckList: allColumns,
			allColumns,
		})

		const filteredRows = getFilteredRows(serverFilter, allRows)
		return filteredRows
	}, [currentRecords, serverFilter, JSON.stringify(allColumns)])

	useEffect(() => {
		const customerKey = environment.account.key
		if (!customerKey) return
		const customerCountRef = ref(database, `userSessions/customerKey/${customerKey}`)
		const viewRef = ref(database, `userSessions/viewActivity/customerKey/${customerKey}`)

		const customerUnsubscribe = onValue(customerCountRef, (snapshot) => {
			// const data = snapshot.val()
			// const currentDateTime = moment().add(serverOffset, 'milliseconds').format('MM/DD/YYYY HH:mm:ss')

			// if (data && areDatesEqual(data?.Updated, currentDateTime, 'MM/DD/YYYY HH:mm:ss')) {
			sleep(2000).then(() => {
				refetchRecords()
			})
			// }
		})

		const viewUnsubscribe = onValue(viewRef, (snapshot) => {
			sleep(2000).then(() => {
				refetchRecords()
			})
		})

		return () => {
			off(customerCountRef)
			off(viewRef)

			customerUnsubscribe()
			viewUnsubscribe()
		}
	}, [environment.account.key])

	return (
		<TileDatagrid
			tile={tile}
			columns={gridColumns}
			rows={currentRows}
			isLoading={gridLoading}
			printTitle={t('common:tabs.activity')}
		/>
	)
}
